import {useCallback, useRef, useState} from 'react';

import {getSegmentElementFromNode} from '../../evidenceModal/selectionUtils';
import {computeNormalizedSelection} from '../../evidenceModal/selectionNormalization';
import {buildSelectionSnippet as buildSelectionSnippetUtil} from '../../evidenceModal/selectionSnippet';

import useSavedSegments from '../../evidenceModal/useSavedSegments';
import {saveSearchResult} from '../../../firebase/firestore/savedSegments';

import {addReference} from '../../prosemirror-editor/yMaps/referencesYMap';
import {
    addFolder as yAddFolder,
    addItem as yAddItem,
    getAllItems
} from '../../prosemirror-editor/yMaps/sourcePackageYMap';
import {createSourcePackageItem} from '../../../utils/api/sourcePackage';
import {useSession} from '../../../firebase/UserProvider';
import useYDocStore from '../../../stores/useYDocStore';


// Owns selection context menu state + cite/save/drag + source package actions.
export default function useDocumentSelectionMenu({
                                                     reportId,
                                                     currentDocKey,
                                                     docData,
                                                     contentRef,
                                                     addOptimisticCited,
                                                 }) {
    // Saved segments for this report/document (deprecated in highlights, still supported)
    const {setSavedSegments} = useSavedSegments(reportId, currentDocKey);

    // Get user for API calls
    const {user} = useSession();

    // Y.Map references for writing citations
    const ydoc = useYDocStore(state => state.ydoc);
    const yReferenceMarks = useYDocStore(state => state.yReferenceMarks);


    // Contextual menu state/refs
    const [menuPosition, setMenuPosition] = useState(null); // { mouseX, mouseY } | null
    const [citeAnchorEl, setCiteAnchorEl] = useState(null); // HTMLElement | null
    const selectedRangeRef = useRef(null);

    const handleContextMenu = useCallback((e) => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
        const range = sel.getRangeAt(0).cloneRange();
        const anchorNode = range.commonAncestorContainer;
        const containerNode = anchorNode.nodeType === 3 ? anchorNode.parentNode : anchorNode;
        if (!contentRef.current || !contentRef.current.contains(containerNode)) return;

        // Ensure both range endpoints are within a segment element
        const ss = getSegmentElementFromNode(contentRef.current, range.startContainer);
        const es = getSegmentElementFromNode(contentRef.current, range.endContainer);
        if (!ss || !es) return;

        e.preventDefault();
        selectedRangeRef.current = range;
        setMenuPosition({mouseX: e.clientX + 2, mouseY: e.clientY - 6});
    }, [contentRef]);

    const handleMenuClose = useCallback(() => {
        setMenuPosition(null);
        setCiteAnchorEl(null);
    }, []);

    // Prevent Ctrl+Click (Windows/Linux/macOS) from clearing selection
    // before the contextmenu event fires
    const handleMouseDown = useCallback((e) => {
        // Check if Ctrl key is pressed (this is the standard for right-click simulation on all platforms)
        // Note: On macOS, Control+Click simulates right-click, NOT Command+Click
        if (!e.ctrlKey) return;

        // Check if there's an existing selection
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;

        const range = sel.getRangeAt(0);
        const containerNode = range.commonAncestorContainer;

        // Only handle if the click is within our content area
        if (!contentRef.current || !contentRef.current.contains(containerNode)) return;

        // Prevent the default mousedown behavior which would clear the selection
        // This allows the contextmenu event to fire with the selection still intact
        e.preventDefault();
    }, [contentRef]);

    const buildSelectionSnippet = (range) =>
        buildSelectionSnippetUtil(range, docData, currentDocKey, contentRef.current);

    async function handleSaveForLater() {
        const range = selectedRangeRef.current;
        if (!range) return handleMenuClose();

        const selectedText = range.toString();
        const norm = computeNormalizedSelection(range, contentRef.current);
        if (!norm) return handleMenuClose();
        const {startIndex: startSegmentIndex, endIndex: endSegmentIndex, startOffset, endOffset} = norm;

        if (reportId && currentDocKey && selectedText) {
            const sI = startSegmentIndex ?? 0;
            const sO = startOffset ?? 0;
            const eI = endSegmentIndex ?? sI;
            const eO = endOffset ?? sO;
            const id = `${currentDocKey}:${sI}:${sO}-${eI}:${eO}`;

            // Prefer a backend numeric segment id when available; fallback to index
            const startSegId =
                docData?.segments?.[sI]?.id ?? docData?.segments?.[sI]?.segmentId ?? sI;

            const segmentData = {
                id,
                type: 'saved',
                docId: currentDocKey,
                docTitle: docData?.docTitle,
                docUrl: docData?.docUrl,
                content: selectedText,
                // New structure fields
                segmentId: startSegId,
                index: sI,
                offset: sO,
                start: {segmentIndex: sI, segmentOffset: sO},
                end: {segmentIndex: eI, segmentOffset: eO},
            };
            try {
                await saveSearchResult(reportId, segmentData);
                setSavedSegments((prev) =>
                    prev?.some((p) => p.id === id) ? prev : [segmentData, ...(prev || [])]
                );
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to save segment:', e);
            }
        }

        const sel = window.getSelection();
        if (sel) sel.removeAllRanges();
        selectedRangeRef.current = null;
        handleMenuClose();
    }

    async function handleCiteToFragment(fragmentUid) {
        try {
            const range = selectedRangeRef.current;
            if (!range || !reportId || !fragmentUid) return handleMenuClose();
            const selectedText = range.toString();
            if (!selectedText) return handleMenuClose();

            const norm = computeNormalizedSelection(range, contentRef.current);
            if (!norm) return handleMenuClose();
            const {startIndex, startOffset, endIndex, endOffset} = norm;

            // Prefer a backend numeric segment id when available; fallback to index
            const startSegId =
                docData?.segments?.[startIndex]?.id ??
                docData?.segments?.[startIndex]?.segmentId ??
                startIndex;

            // Compute localId for optimistic update
            const localId = [currentDocKey, startIndex, startOffset, endIndex, endOffset].join(':');

            const referenceData = {
                localId,
                docId: currentDocKey,
                docUrl: docData?.docUrl,
                docTitle: docData?.docTitle,
                content: selectedText,
                // New structure fields
                segmentId: startSegId,
                index: startIndex,
                offset: startOffset,
                start: {segmentIndex: startIndex, segmentOffset: startOffset},
                end: {segmentIndex: endIndex, segmentOffset: endOffset},
            };

            // Write to Y.Map — instant, no Firestore needed
            if (ydoc && yReferenceMarks) {
                addReference(ydoc, yReferenceMarks, fragmentUid, referenceData);
            } else {
                console.warn('[useDocumentSelectionMenu] Y.Doc not ready, cannot add reference');
            }

            addOptimisticCited?.({
                fragmentUid,
                docId: currentDocKey,
                docTitle: docData?.docTitle,
                docUrl: docData?.docUrl,
                startIndex,
                startOffset,
                endIndex,
                endOffset,
                selectedText,
            });

            // Also add to Source Package root if not already added anywhere
            try {
                const snippet = buildSelectionSnippet(range);
                if (snippet && snippet.localId) {
                    const {ydoc: yDoc, ySourcePackage} = useYDocStore.getState();
                    if (yDoc && ySourcePackage) {
                        const existsAnywhere = getAllItems(ySourcePackage).some((it) => it?.localId === snippet.localId);
                        if (!existsAnywhere) {
                            yAddItem(yDoc, ySourcePackage, {
                                type: 'snippet',
                                parentFolderId: null,
                                ...snippet,
                            });
                        }
                    }
                }
            } catch (_) {
                // ignore add-to-package errors
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to add reference:', e);
        } finally {
            const sel = window.getSelection();
            if (sel) sel.removeAllRanges();
            selectedRangeRef.current = null;
            handleMenuClose();
        }
    }

    // Allow dragging the selected text to Source Package: set dataTransfer with our snippet format
    const handleDragStartSelection = (e) => {
        try {
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
            const range = sel.getRangeAt(0);
            if (!contentRef.current || !contentRef.current.contains(range.commonAncestorContainer)) return;
            const snippet = buildSelectionSnippet(range);
            if (!snippet) return;
            e.dataTransfer?.setData?.('application/x-snippet', JSON.stringify(snippet));
            e.dataTransfer.effectAllowed = 'copy';
        } catch (_) {
            // ignore
        }
    };

    const addSelectionToFolder = (folderId = null) => {
        try {
            const range = selectedRangeRef.current;
            if (!range) return handleMenuClose();
            const snippet = buildSelectionSnippet(range);
            if (!snippet) return handleMenuClose();
            const {ydoc: yDoc, ySourcePackage} = useYDocStore.getState();
            if (yDoc && ySourcePackage) {
                yAddItem(yDoc, ySourcePackage, {
                    type: 'snippet',
                    parentFolderId: folderId ?? null,
                    ...snippet,
                });
            }
        } finally {
            const sel = window.getSelection();
            if (sel) sel.removeAllRanges();
            selectedRangeRef.current = null;
            handleMenuClose();
        }
    };

    const onAddToSourceRoot = () => addSelectionToFolder(null);

    const onAddToSourceFolder = (folderId) => addSelectionToFolder(folderId);

    const onAddToSourceNewFolder = () => {
        try {
            const range = selectedRangeRef.current;
            if (!range) return handleMenuClose();
            const snippet = buildSelectionSnippet(range);
            if (!snippet) return handleMenuClose();
            const {ydoc: yDoc, ySourcePackage} = useYDocStore.getState();
            if (!yDoc || !ySourcePackage) return handleMenuClose();
            const newFolderId = yAddFolder(yDoc, ySourcePackage, {
                name: 'New folder',
                parentFolderId: null,
            });
            yAddItem(yDoc, ySourcePackage, {
                type: 'snippet',
                parentFolderId: newFolderId,
                ...snippet,
            });
        } catch (_) {
            // ignore
        } finally {
            const sel = window.getSelection();
            if (sel) sel.removeAllRanges();
            selectedRangeRef.current = null;
            handleMenuClose();
        }
    };

    // Add selection to another product's source package
    const onAddToAnotherProduct = async (productId, folderId = null) => {
        try {
            const range = selectedRangeRef.current;
            if (!range || !productId) return handleMenuClose();

            const snippet = buildSelectionSnippet(range);
            if (!snippet) return handleMenuClose();

            // Use the API to create the item in another product
            await createSourcePackageItem(productId, {
                type: 'DOCUMENT_FRAGMENT',
                parentFolderId: folderId,
                entity: {
                    docId: snippet.docId,
                    start: snippet.start,
                    end: snippet.end,
                    content: snippet.content,
                    docTitle: snippet.docTitle,
                    docUrl: snippet.docUrl,
                    segmentId: snippet.segmentId,
                },
                userId: user?.uid,
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to add to another product:', e);
        } finally {
            const sel = window.getSelection();
            if (sel) sel.removeAllRanges();
            selectedRangeRef.current = null;
            handleMenuClose();
        }
    };

    return {
        menuPosition,
        citeAnchorEl,
        setCiteAnchorEl,

        selectedRangeRef,

        handleContextMenu,
        handleMouseDown,
        handleMenuClose,
        handleSaveForLater,
        handleCiteToFragment,
        handleDragStartSelection,

        onAddToSourceRoot,
        onAddToSourceFolder,
        onAddToSourceNewFolder,
        onAddToAnotherProduct,
    };
}

